// @flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { EditableFixedDataTable } from 'common/components/core/table';
import { EntityImportTable } from 'common/models/internal/EntityImportTable';
import { translate } from 'react-i18next';
import { ErrorIcon } from 'common/components/core/icon';
import Loader from 'common/components/core/loader/Loader';
import type { List } from 'immutable';
import type { TFunction } from 'react-i18next';
import type { FixedDataTableGetCellPropsTypes } from 'common/components/core/table/FixedDataTable';
import type { EntityErrorTypes } from 'common/entity/generic/EntityImportContainer';
import { ButtonWithPopover, ButtonTypes } from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';

import defaultTheme from './EntityValidationTable.scss';

type Props = {
    theme: Object,
    editedCells: Array<Object>,
    otherValidationsTable: ?EntityImportTable,
    allValidatedEntities: List<*>,
    onTableEdit: (valur: string, cellId: Object) => void,
    entitiesWithErrors?: List<*>,
    entityNameId: string,
    t: TFunction,
    showNonImportableColumns: boolean,
    entityErrors: List<*>,
    getEntityErrorCount: Function,
    getTotalErrorCount: Function,
    updateCellCoordsAndGetError: Function,
    getErrorByKey: Function,
    tableLastRefreshed: string,
    entityErrors: Object,
    bulkValidationInProgress: boolean,
    onScrollEnd: () => void
};

type ErrorNavActionTypes = 'next' | 'prev' | '';

type ErrorKeyTypes = Array<string> | string;

type State = {
    activeErrorCell: ?EntityErrorTypes,
    tableLastRefreshed: string,
    disableErrorNavigation: boolean,
    lastErrCellFocused: boolean,
    errorNavAction: ErrorNavActionTypes,
    firstErrorKey: Array<string>
};

class EntityValidationTable extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            activeErrorCell: null,
            tableLastRefreshed: props.tableLastRefreshed,
            disableErrorNavigation: false,
            lastErrCellFocused: false,
            errorNavAction: '',
            firstErrorKey: []
        };
    }

    static getDerivedStateFromProps(
        { tableLastRefreshed },
        { tableLastRefreshed: prevTableLastRefreshed }
    ) {
        if (prevTableLastRefreshed !== tableLastRefreshed) {
            return {
                activeErrorCell: null,
                tableLastRefreshed
            };
        }
        return null;
    }

    getCellProps = ({
        row,
        rowId,
        header,
        rowIndex,
        colIndex
    }: FixedDataTableGetCellPropsTypes): {
        error: string,
        isErrorLoading: boolean,
        isActive: boolean,
        setRef?: Function,
        onFocus: Function,
        onBlur?: Function
    } => {
        const { entityErrors, updateCellCoordsAndGetError } = this.props;
        const { activeErrorCell } = this.state;

        const entity = row;
        //isLoading when new validation data is getting fetched
        const { errorMsg, isLoading } = updateCellCoordsAndGetError({
            rowIndex,
            colIndex,
            headerId: header.id,
            rowId
        });
        let asyncError;
        let asyncErrorIsLoading = false;
        if (entityErrors) {
            ({
                error: asyncError,
                isLoading: asyncErrorIsLoading
            } = entityErrors.getIn([entity.id, header.id], {}));
        }
        const otherProps = {};
        if (
            activeErrorCell &&
            activeErrorCell.cellCoords &&
            activeErrorCell.cellCoords.length &&
            activeErrorCell.cellCoords[0] === rowIndex &&
            activeErrorCell.cellCoords[1] === colIndex
        ) {
            otherProps.setRef = ref => {
                ref?.refs?.wrappedInstance.focus();
            };
            otherProps.isActive = true;
            if (
                this.errorKeysEquality(activeErrorCell.nextErrKey, [
                    rowId,
                    header.id
                ])
            ) {
                otherProps.onBlur = this.handelCellBlur;
            }
        }

        otherProps.onFocus = () =>
            this.handelCellFocus({
                headerId: header.id,
                rowId,
                hasError: !!errorMsg || !!asyncError
            });

        return {
            error: errorMsg || asyncError,
            isErrorLoading: asyncErrorIsLoading || isLoading,
            greyedOutBG: !header.importable,
            ...otherProps
        };
    };

    handelCellBlur = (): void =>
        this.setState({ disableErrorNavigation: false, activeErrorCell: null });

    setActiveCell = (
        errorKey: ErrorKeyTypes,
        errorNavAction: ErrorNavActionTypes = ''
    ): void => {
        const { getErrorByKey } = this.props;
        const lastError = getErrorByKey(['lastErrKey']);
        const firstErrorKey = getErrorByKey(['firstErrKey']);
        const nextError = getErrorByKey(errorKey);
        this.setState({
            activeErrorCell: { ...nextError, key: errorKey },
            disableErrorNavigation: this.errorKeysEquality(
                nextError.nextErrKey,
                errorKey
            ),
            firstErrorKey,
            lastErrCellFocused: this.errorKeysEquality(lastError, errorKey),
            errorNavAction
        });
    };

    handelCellFocus = ({
        hasError,
        headerId,
        rowId
    }: {
        hasError: boolean,
        headerId: string,
        rowId: string
    }): void => {
        if (hasError) {
            // get error object of selected error cell
            this.setActiveCell([rowId, headerId]);
        } else {
            const { activeErrorCell } = this.state;
            // reset error state so next time error will start from first col again
            if (activeErrorCell) {
                this.setState({
                    activeErrorCell: null
                });
            }
        }
    };

    errorKeysEquality = (
        nextErrKey: ErrorKeyTypes,
        errorKey: ErrorKeyTypes
    ): boolean => {
        if (nextErrKey[0] === errorKey[0] && nextErrKey[1] === errorKey[1]) {
            return true;
        }
        return false;
    };

    focusNextError = (): void => {
        const { activeErrorCell } = this.state;
        const { getErrorByKey } = this.props;
        let nextErrKey: ErrorKeyTypes;
        if (activeErrorCell === null) {
            // When no errors are focused pick first error and focus
            nextErrKey = getErrorByKey(['firstErrKey']);
        } else {
            // Pick next error and show
            ({ nextErrKey } = activeErrorCell);
        }
        this.setActiveCell(nextErrKey, 'next');
    };

    focusPrevError = (): void => {
        const { activeErrorCell } = this.state;
        const { getErrorByKey } = this.props;
        let prevErrKey: ErrorKeyTypes;
        if (activeErrorCell === null) {
            // When no errors are focused pick last error and focus
            prevErrKey = getErrorByKey(['lastErrKey']);
        } else {
            // Pick prev error and show
            ({ prevErrKey } = activeErrorCell);
        }
        this.setActiveCell(prevErrKey, 'prev');
    };

    handelPreventBlur = (e: SyntheticEvent<HTMLDivElement>): void =>
        e.preventDefault();

    render() {
        const {
            otherValidationsTable,
            theme,
            onTableEdit,
            entityNameId,
            t,
            bulkValidationInProgress,
            showNonImportableColumns,
            getEntityErrorCount,
            getTotalErrorCount,
            onScrollEnd,
            tableLastRefreshed,
            fetchPage
        } = this.props;
        const {
            activeErrorCell,
            disableErrorNavigation,
            errorNavAction,
            lastErrCellFocused,
            firstErrorKey
        } = this.state;

        let activeColIndex;
        let activeRowIndex;
        let preventPrevError = false;
        if (activeErrorCell) {
            ({
                cellCoords: [activeRowIndex, activeColIndex]
            } = activeErrorCell);
            preventPrevError = this.errorKeysEquality(
                firstErrorKey,
                activeErrorCell.key
            );
        } else {
            preventPrevError = true;
        }

        if (bulkValidationInProgress) {
            return <Loader active absolute theme={theme} />;
        }

        const {
            rows: rowsErrorCount,
            total: totalErrors
        } = getTotalErrorCount();
        if (activeColIndex) {
            if (
                (errorNavAction === 'next' && activeColIndex > 6) ||
                (errorNavAction === 'prev' && lastErrCellFocused)
            )
                /* +2 is hack for fixing
                 * When table scrolls table shows empty space at last
                 * Keeps scrolled cell in bit center instead of keeping on corner
                 */
                activeColIndex += 2;
        }
        const preventErrorNavigation =
            rowsErrorCount === 0 || disableErrorNavigation;

        const totalCount = otherValidationsTable.rowsSize;
        return (
            <div className={theme.entityValidationTableWrap}>
                <div className={theme.header}>
                    <div className={theme.errorInfo}>
                        <div className={theme.tableTitle}>
                            {t('entityCount', {
                                count: totalCount,
                                entityNameId
                            })}
                        </div>
                        {rowsErrorCount > 0 ? (
                            <div className={theme.errorText}>
                                <ErrorIcon visible theme={theme} />
                                {t(
                                    'entityImport.rowErrorMessageWithTotalCount',
                                    {
                                        count: rowsErrorCount,
                                        totalCount: totalErrors
                                    }
                                )}
                            </div>
                        ) : null}
                    </div>
                    <div
                        className={theme.errorActions}
                        onMouseDown={
                            disableErrorNavigation && this.handelPreventBlur
                            // prevent when only one error is there that time input focuses out on disabled button click
                        }
                    >
                        <ButtonWithPopover
                            ripple={!preventErrorNavigation}
                            disabled={
                                preventPrevError || preventErrorNavigation
                            }
                            theme={theme}
                            icon="arrow-left"
                            color={dashboardColors.slate.name}
                            buttonType={ButtonTypes.Icon}
                            triggerPopoverOn={PopoverTriggerEnum.Hover}
                            popOverContent={
                                !preventErrorNavigation &&
                                t('entityImport.jumpToPrevErrorMessage')
                            }
                            onClick={this.focusPrevError}
                            className={theme.errorActionBtn}
                        />
                        <ButtonWithPopover
                            ripple={!preventErrorNavigation}
                            disabled={preventErrorNavigation}
                            color={dashboardColors.slate.name}
                            theme={theme}
                            icon="arrow-right"
                            buttonType={ButtonTypes.Icon}
                            triggerPopoverOn={PopoverTriggerEnum.Hover}
                            popOverContent={
                                !preventErrorNavigation &&
                                t('entityImport.jumpToNextErrorMessage')
                            }
                            onClick={this.focusNextError}
                            className={theme.errorActionBtn}
                        />
                    </div>
                </div>
                <EditableFixedDataTable
                    theme={theme}
                    tableData={otherValidationsTable}
                    onTableEdit={onTableEdit}
                    getCellProps={this.getCellProps}
                    tableLastRefreshed={tableLastRefreshed}
                    showNonImportableColumns={showNonImportableColumns}
                    getEntityErrorCount={getEntityErrorCount}
                    scrollToColumn={activeColIndex}
                    scrollToRow={activeRowIndex}
                    onScrollEnd={onScrollEnd}
                    rowsSize={otherValidationsTable.rowsSize}
                    fetchPage={fetchPage}
                />
            </div>
        );
    }
}

export default translate('entityManagement')(
    themr('EntityValidationTable', defaultTheme)(EntityValidationTable)
);
